import * as $ from 'jquery';

import { elementData } from '../util';

const TABLE_CONTAINER_SELECTOR = '.js-table-container';
const EDITOR_CONTAINER_SELECTOR = '.main-editor';
const EMBED_CONTAINER_SELECTOR = '.mode-embed';

/**
 * Column Select Plugin
 *
 * Delegates column selection to the table column header
 * click events.  Also registers a global click event to
 * handle column deselection
 * @todo prevent plugin from depending on foreign container selectors
 */
export default class ColumnSelect {
  static NAMESPACE = 'axel-column-select';
  static IS_SELECTED_CLASS = 'is-selected-col';

  private opts;
  public label;
  private state;
  private renderer;
  private ui;
  private $table;
  private $container;
  private $body;

  constructor(renderer, opts) {
    this.renderer = renderer;
    this.ui = {
      colHeader: 'thead th.js-col-header',
    };
    this.opts = opts;
    this.label = 'column-select';
    // fallback state
    this.state = {
      selected: null,
    };
  }

  /**
   * [HookMethod] Initialize
   * @param _
   * @param containerEl
   */
  public initialize(_, containerEl) {
    this.$container = $(containerEl);
    this.$body = this.$container.parents('body');
  }

  /**
   * [HookMethod] Build Table
   *
   * @param tableEl
   * @returns {Element}
   */
  public buildTable(tableEl) {
    this.$table = $(tableEl);
    // add plugin class
    this.$table.addClass(ColumnSelect.NAMESPACE);
    return tableEl;
  }

  /**
   * [HookMethod] After Render
   *
   * @param tableEl
   * @returns {Element}
   */
  public afterRender(tableEl) {
    this.initializeTableHeaders(this.$table);
    this.initializeTableClickHandler(this.$table);
    return tableEl;
  }

  public destroy() {
    this.$body.off(this.namespace('click'));
    this.$table.find(this.ui.colHeader).off(this.namespace('click'));
  }

  /**
   * Initialize Table Headers
   *
   * Main method that delegates column selection to table header
   * click events as well as render initial column selection
   * @todo support header children click events by attaching to the <table> instead
   * @todo [..continued] this will prevent the domain violation in column-sort plugin
   * @param $table
   */
  private initializeTableHeaders($table) {
    const $columnHeaders = $table.find(this.ui.colHeader);
    const tableHeaderClickHandler = this.tableHeaderClickHandler.bind(this);
    const selectedColumn = this.getSelectedColumn();

    // select initial column
    if (selectedColumn) {
      this.highlightColumn(selectedColumn);
    }

    $columnHeaders.on(this.namespace('click'), tableHeaderClickHandler);
  }

  private initializeTableClickHandler($table) {
    $table.on(this.namespace('click'), this.tableCellClickHandler.bind(this));
  }

  private namespace(evtName) {
    return evtName + '.' + ColumnSelect.NAMESPACE;
  }

  /**
   * Table Header Click Handler
   *
   * Updates column selection state and highlights the column in the DOM
   * @param evt
   */
  private tableHeaderClickHandler(evt) {
    const oldIndex = this.getSelectedColumn();
    const colIndex = elementData($(evt.target).closest('th').get(0), 'column');

    this.setSelectedColumn(colIndex);
    this.highlightColumn(colIndex, oldIndex);
  }

  /**
   * Table Cell Click Handler
   *
   * Unselect column when unselected table cell clicked
   * @todo refactor isRowHeader conditional to be more explicit (may have to add new HTML attr)
   * @param evt
   */
  private tableCellClickHandler(evt) {
    const selectedColumn = this.getSelectedColumn();
    const columnIndex = elementData(evt.target, 'column');
    const rowIndex = elementData(evt.target, 'row');
    const tagName = evt.target.tagName.toLowerCase();
    const isRowCellInUnselectedColumn = rowIndex && columnIndex !== selectedColumn;
    const isRowHeader = !rowIndex && !columnIndex && tagName === 'th';

    if (isRowCellInUnselectedColumn || isRowHeader) {
      this.unselectColumn();
    }
  }

  private getSelectedColumn() {
    if (this.opts.selectedCol instanceof Function) {
      return this.opts.selectedCol();
    } else {
      return this.state.selected;
    }
  }

  private setSelectedColumn(index) {
    if (this.opts.selectCol instanceof Function) {
      return this.opts.selectCol(index);
    } else {
      return (this.state.selected = index);
    }
  }

  /**
   * Highlight Column
   *
   * Add "selected" class to all column cells.  Also deselect
   * previous column if available
   * @param newIndex
   * @param oldIndex
   */
  private highlightColumn(newIndex, oldIndex = null) {
    const $table = this.$table;
    const $body = this.$body;
    const globalClickHandler = this.globalClickHandler.bind(this);
    const clickEventLabel = this.namespace('click');

    if (oldIndex) {
      this.unhighlightColByIndex(oldIndex);
    }

    this.findCellsByColumnIndex(newIndex, $table).addClass(ColumnSelect.IS_SELECTED_CLASS);
    $body.off(clickEventLabel);
    $body.on(clickEventLabel, globalClickHandler);
  }

  /**
   * Find Cell By Column Index
   *
   * @param index
   * @param $table
   * @returns {any}
   */
  private findCellsByColumnIndex(index, $table) {
    return $table.find('.col-' + index);
  }

  /**
   * Global Click Handler
   *
   * Resets selected state for qualified global event
   * @param evt
   */
  private globalClickHandler(evt) {
    const $body = this.$body;
    const clickEventLabel = this.namespace('click');
    if (this.validDeadzoneClickEvent(evt)) {
      this.unselectColumn();
      $body.off(clickEventLabel);
    }
  }

  /**
   * Valid Deadzone Click Event
   *
   * Determines if the click event is between the table and
   * it relevant contextual container
   * @param evt
   * @returns {boolean}
   */
  private validDeadzoneClickEvent(evt) {
    const isClickTargetParent = this.elementParentSeeker(evt.target);
    const childOfTableContainer = isClickTargetParent(TABLE_CONTAINER_SELECTOR);
    const childOfEditorContainer =
      isClickTargetParent(EDITOR_CONTAINER_SELECTOR) || isClickTargetParent(EMBED_CONTAINER_SELECTOR);

    return !childOfTableContainer && childOfEditorContainer;
  }

  /**
   * Element Parent Seeker
   *
   * Curried callback that traverses an elements dom path
   * for a parent that matches the given selector
   * @param el
   * @returns {function(any): boolean}
   */
  private elementParentSeeker(el) {
    return function seekParent(parentSelector) {
      return !!$(el).closest(parentSelector).length;
    };
  }

  private unselectColumn() {
    this.unhighlightColByIndex(this.getSelectedColumn());
    this.setSelectedColumn(null);
  }

  private unhighlightColByIndex(index) {
    this.findCellsByColumnIndex(index, this.$table).removeClass(ColumnSelect.IS_SELECTED_CLASS);
  }
}
